import { saludoBienvenida } from "../../utilidades/funciones/saludo";
import { esAmbientePruebas } from "../../compartido/ui/AvisoAmbiente";
import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Button,
  MenuItem,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { AccesoServicio } from "../../servicios/publicos/AccesoServicio";
import { CatalogosServicio } from "../../servicios/publicos/CatalogosServicio";
import { useFormulario } from "../../utilidades/funciones/UsoFormulario";
import { crearMensaje } from "../../utilidades/funciones/mensaje";
import { tokenHelper } from "../../utilidades/auth/tokenHelper";
import { decodeToken } from "../../utilidades/auth/usuarioToken";
import { FormCard } from "../../compartido/ui/FormCard";
import { CampoTexto } from "../../compartido/ui/CampoTexto";
import { CampoAutocompletar, OpcionCatalogo } from "../../compartido/ui/CampoAutocompletar";
import { BotonPrincipal } from "../../compartido/ui/BotonPrincipal";

type TipoPersona = "NATURAL" | "JURIDICA";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const SEXOS = [
  { value: 1, label: "Masculino" },
  { value: 2, label: "Femenino" },
];

const Registro = () => {
  const navigate = useNavigate();
  const [tipoPersona, setTipoPersona] = useState<TipoPersona>("NATURAL");
  const [enProceso, setEnProceso] = useState(false);

  const {
    nombreUsuario,
    telefonoUsuario,
    correoUsuario,
    claveAcceso,
    confirmarClave,
    dobleEnlace: dobleEnlaceBase,
  } = useFormulario({
    nombreUsuario: "",
    telefonoUsuario: "",
    correoUsuario: "",
    claveAcceso: "",
    confirmarClave: "",
  });

  const {
    cedula,
    fechaNacimientoUsuario,
    sexoBiologico,
    direccionResidencia,
    dobleEnlace: dobleEnlaceNatural,
  } = useFormulario({
    cedula: "",
    fechaNacimientoUsuario: "",
    sexoBiologico: "",
    direccionResidencia: "",
  });

  const {
    razonSocial,
    nit,
    representanteLegal,
    tipoEmpresa,
    dobleEnlace: dobleEnlaceJuridica,
  } = useFormulario({
    razonSocial: "",
    nit: "",
    representanteLegal: "",
    tipoEmpresa: "",
  });

  // ── Catálogos públicos: residencia (ciudad → barrio), listas con buscador ──
  const [ciudades, setCiudades] = useState<OpcionCatalogo[]>([]);
  const [barrios, setBarrios] = useState<OpcionCatalogo[]>([]);
  const [cargandoCiudades, setCargandoCiudades] = useState(true);
  const [cargandoBarrios, setCargandoBarrios] = useState(false);
  const [ciudadNacimientoId, setCiudadNacimientoId] = useState<number | "">("");
  // La ciudad de residencia solo filtra los barrios; lo que se guarda es el barrio.
  const [ciudadResidenciaId, setCiudadResidenciaId] = useState<number | "">("");
  const [barrioResidenciaId, setBarrioResidenciaId] = useState<number | "">("");

  // Nacimiento: departamento → municipio (la capital aparece de primera).
  const [departamentos, setDepartamentos] = useState<OpcionCatalogo<string>[]>([]);
  const [municipios, setMunicipios] = useState<OpcionCatalogo[]>([]);
  const [cargandoDepartamentos, setCargandoDepartamentos] = useState(true);
  const [cargandoMunicipios, setCargandoMunicipios] = useState(false);
  const [departamentoNacimiento, setDepartamentoNacimiento] = useState<string | "">("");

  useEffect(() => {
    CatalogosServicio.departamentos()
      .then(setDepartamentos)
      .catch(() => crearMensaje("error", "No se pudo cargar la lista de departamentos"))
      .finally(() => setCargandoDepartamentos(false));
  }, []);

  useEffect(() => {
    setCiudadNacimientoId("");
    setMunicipios([]);
    if (departamentoNacimiento === "") return;
    setCargandoMunicipios(true);
    CatalogosServicio.municipios(departamentoNacimiento)
      .then(setMunicipios)
      .catch(() => crearMensaje("error", "No se pudo cargar la lista de municipios"))
      .finally(() => setCargandoMunicipios(false));
  }, [departamentoNacimiento]);

  useEffect(() => {
    CatalogosServicio.ciudadesConBarrios()
      .then((lista) => {
        setCiudades(lista);
        // Por defecto, residencia en Tunja si existe en el catálogo.
        const tunja = lista.find((c) => c.nombre.toLowerCase() === "tunja");
        if (tunja) setCiudadResidenciaId(tunja.codigo);
      })
      .catch(() => crearMensaje("error", "No se pudo cargar la lista de ciudades"))
      .finally(() => setCargandoCiudades(false));
  }, []);

  useEffect(() => {
    setBarrioResidenciaId("");
    setBarrios([]);
    if (ciudadResidenciaId === "") return;
    setCargandoBarrios(true);
    CatalogosServicio.barrios(ciudadResidenciaId)
      .then(setBarrios)
      .catch(() => crearMensaje("error", "No se pudo cargar la lista de barrios"))
      .finally(() => setCargandoBarrios(false));
  }, [ciudadResidenciaId]);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();

    if (claveAcceso !== confirmarClave) {
      crearMensaje("warning", "Las contraseñas no coinciden");
      return;
    }
    if (!PASSWORD_REGEX.test(claveAcceso)) {
      crearMensaje(
        "warning",
        "La contraseña debe tener mínimo 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial (@$!%*?&)"
      );
      return;
    }
    if (tipoPersona === "NATURAL" && !/^.{7,20}$/.test(cedula.trim())) {
      crearMensaje("warning", "La cédula debe tener entre 7 y 20 caracteres");
      return;
    }
    if (tipoPersona === "NATURAL" && (ciudadNacimientoId === "" || barrioResidenciaId === "")) {
      crearMensaje("warning", "Selecciona el municipio de nacimiento y el barrio de residencia");
      return;
    }
    if (tipoPersona === "JURIDICA" && !/^.{9,20}$/.test(nit.trim())) {
      crearMensaje("warning", "El NIT debe tener entre 9 y 20 caracteres");
      return;
    }

    setEnProceso(true);
    try {
      const payload: Record<string, any> = {
        tipoPersona,
        nombreUsuario,
        telefonoUsuario,
        correoUsuario,
        claveAcceso,
      };

      if (tipoPersona === "NATURAL") {
        Object.assign(payload, {
          cedula,
          fechaNacimientoUsuario,
          sexoBiologico: Number(sexoBiologico),
          ciudadNacimientoId: Number(ciudadNacimientoId),
          barrioResidenciaId: Number(barrioResidenciaId),
          direccionResidencia,
        });
      } else {
        Object.assign(payload, { razonSocial, nit, representanteLegal, tipoEmpresa });
      }

      const respuesta = await AccesoServicio.registrarUsuario(payload);
      const token = respuesta?.token;
      if (!token) throw new Error("TOKEN_NOT_FOUND");
      const datosToken = decodeToken(token);
      if (!datosToken) throw new Error("TOKEN_INVALIDO");
      tokenHelper.set(token);
      crearMensaje("success", `¡Cuenta creada! ${saludoBienvenida(datosToken.sexo_biologico)}, ${datosToken.name}`);
      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      crearMensaje("error", error.message || "Error al registrar la cuenta");
    } finally {
      setEnProceso(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        py: 6,
        px: 2,
      }}
    >
      <FormCard
        titulo="Crear cuenta"
        subtitulo="Regístrate para acceder a la Ventanilla Única de Construcción"
        maxWidth={640}
      >
        {esAmbientePruebas && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <strong>Ambiente de pruebas.</strong> No ingreses datos personales reales: usa nombres,
            cédulas y direcciones ficticias. Esta información puede borrarse en cualquier momento.
          </Alert>
        )}
        <ToggleButtonGroup
          value={tipoPersona}
          exclusive
          onChange={(_, val) => val && setTipoPersona(val)}
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton value="NATURAL" sx={{ fontWeight: 700 }}>
            Persona Natural
          </ToggleButton>
          <ToggleButton value="JURIDICA" sx={{ fontWeight: 700 }}>
            Persona Jurídica
          </ToggleButton>
        </ToggleButtonGroup>

        <Box component="form" onSubmit={enviar}>
          <Stack spacing={2.5}>
            {/* Campos comunes */}
            <CampoTexto
              label="Nombre completo"
              name="nombreUsuario"
              value={nombreUsuario}
              onChange={dobleEnlaceBase}
              required
            />
            <Grid container spacing={2}>
              <Grid size={6}>
                <CampoTexto
                  label="Teléfono"
                  name="telefonoUsuario"
                  value={telefonoUsuario}
                  onChange={dobleEnlaceBase}
                  required
                />
              </Grid>
              <Grid size={6}>
                <CampoTexto
                  label="Correo electrónico"
                  name="correoUsuario"
                  type="email"
                  value={correoUsuario}
                  onChange={dobleEnlaceBase}
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={6}>
                <CampoTexto
                  label="Contraseña"
                  name="claveAcceso"
                  autoComplete="new-password"
                  type="password"
                  value={claveAcceso}
                  onChange={dobleEnlaceBase}
                  required
                />
              </Grid>
              <Grid size={6}>
                <CampoTexto
                  label="Confirmar contraseña"
                  name="confirmarClave"
                  autoComplete="new-password"
                  type="password"
                  value={confirmarClave}
                  onChange={dobleEnlaceBase}
                  required
                />
              </Grid>
            </Grid>

            {/* Persona Natural */}
            {tipoPersona === "NATURAL" && (
              <>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <CampoTexto
                      label="Cédula"
                      name="cedula"
                      autoComplete="username"
                      value={cedula}
                      onChange={dobleEnlaceNatural}
                      required
                    />
                  </Grid>
                  <Grid size={6}>
                    <CampoTexto
                      label="Fecha de nacimiento"
                      name="fechaNacimientoUsuario"
                      type="date"
                      value={fechaNacimientoUsuario}
                      onChange={dobleEnlaceNatural}
                      required
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CampoAutocompletar
                      label="Departamento de nacimiento"
                      opciones={departamentos}
                      valor={departamentoNacimiento}
                      onCambio={setDepartamentoNacimiento}
                      cargando={cargandoDepartamentos}
                      required
                      textoSinOpciones="No hay departamentos con ese nombre"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CampoAutocompletar
                      label="Municipio de nacimiento"
                      opciones={municipios}
                      valor={ciudadNacimientoId}
                      onCambio={setCiudadNacimientoId}
                      cargando={cargandoMunicipios}
                      disabled={departamentoNacimiento === ""}
                      helperText={departamentoNacimiento === "" ? "Primero elige el departamento" : undefined}
                      required
                      textoSinOpciones="No hay municipios con ese nombre"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <CampoTexto
                      label="Sexo biológico"
                      name="sexoBiologico"
                      select
                      value={sexoBiologico}
                      onChange={dobleEnlaceNatural}
                      required
                    >
                      {SEXOS.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </CampoTexto>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <CampoAutocompletar
                      label="Ciudad de residencia"
                      opciones={ciudades}
                      valor={ciudadResidenciaId}
                      onCambio={setCiudadResidenciaId}
                      cargando={cargandoCiudades}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12 }}>
                    <CampoAutocompletar
                      label="Barrio de residencia"
                      opciones={barrios}
                      valor={barrioResidenciaId}
                      onCambio={setBarrioResidenciaId}
                      cargando={cargandoBarrios}
                      disabled={ciudadResidenciaId === ""}
                      required
                      textoSinOpciones="No hay barrios con ese nombre"
                    />
                  </Grid>
                </Grid>
                <CampoTexto
                  label="Dirección de residencia"
                  name="direccionResidencia"
                  value={direccionResidencia}
                  onChange={dobleEnlaceNatural}
                  required
                />
              </>
            )}

            {/* Persona Jurídica */}
            {tipoPersona === "JURIDICA" && (
              <>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <CampoTexto
                      label="Razón social"
                      name="razonSocial"
                      value={razonSocial}
                      onChange={dobleEnlaceJuridica}
                      required
                    />
                  </Grid>
                  <Grid size={6}>
                    <CampoTexto
                      label="NIT"
                      name="nit"
                      autoComplete="username"
                      value={nit}
                      onChange={dobleEnlaceJuridica}
                      required
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <CampoTexto
                      label="Representante legal"
                      name="representanteLegal"
                      value={representanteLegal}
                      onChange={dobleEnlaceJuridica}
                      required
                    />
                  </Grid>
                  <Grid size={6}>
                    <CampoTexto
                      label="Tipo de empresa"
                      name="tipoEmpresa"
                      value={tipoEmpresa}
                      onChange={dobleEnlaceJuridica}
                      required
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <BotonPrincipal cargando={enProceso}>Crear cuenta</BotonPrincipal>

            <Button
              variant="text"
              onClick={() => navigate("/login")}
              sx={{ textTransform: "none", fontSize: "0.9rem" }}
            >
              ¿Ya tienes cuenta? Inicia sesión
            </Button>
          </Stack>
        </Box>
      </FormCard>
    </Box>
  );
};

export default Registro;
